import { QueryKey } from '@tanstack/react-query'
import { queryKeys } from './queryKeys'

/**
 * The realtime invalidation policy: the single map from each Socket.IO event to
 * the react-query cache keys it invalidates.
 *
 * Previously every screen subscribed to socket events and re-typed the keys to
 * invalidate inline, so the event→key mapping was implicit and spread across
 * pages. Centralizing it here — sourced from the same {@link queryKeys} factory
 * the owning query hooks use — means the cache key knowledge stays in the data
 * layer, the policy is testable in isolation, and screens drop their handlers.
 */

/** Socket.IO events that drive react-query cache invalidation. */
export type RealtimeEvent =
	| 'trip_updated'
	| 'itinerary_updated'
	| 'travel_segments_updated'
	| 'idea_updated'

/** Payload shared by every realtime event: the trip the change belongs to. */
export interface RealtimePayload {
	tripId?: string
}

/** Every realtime event, for a single subscribe-once loop. */
export const REALTIME_EVENTS: RealtimeEvent[] = [
	'trip_updated',
	'itinerary_updated',
	'travel_segments_updated',
	'idea_updated',
]

/**
 * The cache keys a given realtime event invalidates, scoped to the event's own
 * `tripId`. Invalidation refetches only keys with a mounted observer; an inactive
 * key is marked stale and refetched the next time it mounts — so the map stays
 * deliberately narrow (e.g. trip_updated does not also invalidate the trips list)
 * to avoid forcing refetches the prior per-screen handlers never triggered.
 */
export const realtimeInvalidationKeys = (
	event: RealtimeEvent,
	payload: RealtimePayload,
): QueryKey[] => {
	const { tripId } = payload
	switch (event) {
		case 'trip_updated':
			// Only the trip's own detail — not the trips() list. These events are
			// delivered solely in the trip room (which only the itinerary screen
			// joins), so the list is never the active query when one arrives;
			// mutations already invalidate trips() on the acting client.
			return [queryKeys.trip(tripId)]
		case 'itinerary_updated':
			return queryKeys.itineraryContentKeys(tripId)
		case 'travel_segments_updated':
			return [queryKeys.travelSegments(tripId)]
		case 'idea_updated':
			return [queryKeys.ideas(tripId)]
	}
}

/** Where realtime invalidations go through, so a surface can hold them back for a moment. */
export interface InvalidationGate {
	/** Invalidate the key now, or on release when something is holding the gate. */
	invalidate: (key: QueryKey) => void
	/** Hold everything that arrives from here on; the returned release applies it. Holds stack, and a release counts once. */
	hold: () => () => void
}

/**
 * A gate in front of realtime invalidation (ADR 0009). Invalidation is
 * unconditional for everyone in a trip room, so a co-traveller's edit would
 * re-render a list under an active drag; the surface that owns the drag holds
 * the gate for as long as the card is in the air and no longer. Keys that
 * arrive meanwhile are kept — each once — and invalidated on release.
 */
export const createInvalidationGate = (invalidate: (key: QueryKey) => void): InvalidationGate => {
	let holds = 0
	const held = new Map<string, QueryKey>()

	return {
		invalidate: key => {
			if (holds === 0) invalidate(key)
			else held.set(JSON.stringify(key), key)
		},
		hold: () => {
			holds++
			let released = false
			return () => {
				if (released) return
				released = true
				holds--
				if (holds > 0) return
				const keys = [...held.values()]
				held.clear()
				keys.forEach(invalidate)
			}
		},
	}
}
